<template>
    <div v-if="show" class="rate-limit-indicator">
        <div class="card border-0 bg-light">
            <div class="card-body py-2 px-3">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="rate-limit-info">
                        <small class="text-muted">
                            <i :class="iconClass"></i>
                            {{ statusText }}
                        </small>
                    </div>
                    
                    <div class="rate-limit-visual">
                        <!-- Progress bar showing remaining requests -->
                        <div class="progress" style="width: 80px; height: 6px;">
                            <div 
                                class="progress-bar"
                                :class="progressBarClass"
                                role="progressbar"
                                :style="{ width: progressPercentage + '%' }"
                                :aria-valuenow="remaining"
                                :aria-valuemin="0"
                                :aria-valuemax="maxRequests"
                                :aria-label="`${remaining} requests remaining out of ${maxRequests}`"
                            ></div>
                        </div>
                    </div>
                </div>
                
                <!-- Detailed breakdown (only shown when expanded or low on requests) -->
                <div v-if="showDetails" class="rate-limit-details mt-2">
                    <div class="row text-center">
                        <div class="col-4">
                            <div class="stat-value">{{ remaining }}</div>
                            <div class="stat-label">Remaining</div>
                        </div>
                        <div class="col-4">
                            <div class="stat-value">{{ maxRequests }}</div>
                            <div class="stat-label">Per Minute</div>
                        </div>
                        <div class="col-4">
                            <div class="stat-value">{{ resetTime }}</div>
                            <div class="stat-label">Reset</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RateLimitIndicator',
    
    props: {
        remaining: {
            type: Number,
            default: 10
        },
        maxRequests: {
            type: Number,
            default: 10
        },
        show: {
            type: Boolean,
            default: true
        },
        resetTimeMinutes: {
            type: Number,
            default: 1
        }
    },
    
    data() {
        return {
            expanded: false
        }
    },
    
    computed: {
        progressPercentage() {
            if (this.maxRequests === 0) return 0
            return Math.max(0, (this.remaining / this.maxRequests) * 100)
        },
        
        statusLevel() {
            const percentage = this.progressPercentage
            if (percentage === 0) return 'exhausted'
            if (percentage <= 20) return 'low'
            if (percentage <= 50) return 'medium'
            return 'good'
        },
        
        statusText() {
            switch (this.statusLevel) {
                case 'exhausted':
                    return 'Rate limit reached - please wait'
                case 'low':
                    return `${this.remaining} questions remaining`
                case 'medium':
                    return `${this.remaining} questions left`
                case 'good':
                default:
                    return `${this.remaining}/${this.maxRequests} questions available`
            }
        },
        
        iconClass() {
            switch (this.statusLevel) {
                case 'exhausted':
                    return 'fas fa-ban text-danger'
                case 'low':
                    return 'fas fa-exclamation-triangle text-warning'
                case 'medium':
                    return 'fas fa-clock text-info'
                case 'good':
                default:
                    return 'fas fa-check-circle text-success'
            }
        },
        
        progressBarClass() {
            switch (this.statusLevel) {
                case 'exhausted':
                    return 'bg-danger'
                case 'low':
                    return 'bg-warning'
                case 'medium':
                    return 'bg-info'
                case 'good':
                default:
                    return 'bg-success'
            }
        },
        
        showDetails() {
            return this.expanded || this.statusLevel === 'low' || this.statusLevel === 'exhausted'
        },
        
        resetTime() {
            if (this.resetTimeMinutes < 1) {
                return '< 1m'
            }
            return `${this.resetTimeMinutes}m`
        }
    },
    
    methods: {
        toggleExpanded() {
            this.expanded = !this.expanded
        }
    }
}
</script>

<style scoped>
.rate-limit-indicator {
    /* Main container styling */
}

.card {
    transition: all 0.2s ease;
    cursor: pointer;
}

.card:hover {
    background-color: #e9ecef !important;
}

.rate-limit-info {
    /* Info section styling */
}

.rate-limit-visual {
    /* Visual progress bar section */
}

.progress {
    border-radius: 3px;
    background-color: #e9ecef;
}

.progress-bar {
    transition: width 0.6s ease, background-color 0.3s ease;
    border-radius: 3px;
}

.rate-limit-details {
    border-top: 1px solid #dee2e6;
    padding-top: 0.5rem;
    margin-top: 0.5rem;
}

.stat-value {
    font-weight: 600;
    font-size: 0.875rem;
    color: #495057;
}

.stat-label {
    font-size: 0.75rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Responsive adjustments */
@media (max-width: 576px) {
    .d-flex {
        flex-direction: column;
        align-items: stretch !important;
    }
    
    .rate-limit-visual {
        margin-top: 0.5rem;
    }
    
    .progress {
        width: 100% !important;
        height: 8px !important;
    }
}

/* Animation for details expansion */
.rate-limit-details {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .card,
    .progress-bar,
    .rate-limit-details {
        transition: none;
        animation: none;
    }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
    .progress {
        border: 1px solid;
    }
    
    .progress-bar {
        border: 1px solid;
    }
}

/* Focus management for accessibility */
.card:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
}
</style>


// WEBPACK FOOTER //
// src/Assets/components/AiAgents/CustomerService/components/RateLimitIndicator.vue